import React, { useEffect, useState } from 'react'
import AddProduct from './AddProduct';
import Button from '@mui/material/Button';
import IconButton from '@mui/joy/IconButton';
import BasicInformation from './BasicInformation';
import ApprovalPersonsTable from './ApprovalPersonsTable';
import LoadingIcon from '../../../../../../common/LoadingIcon';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ToastMessage from '../../../../../../common/ToastMessage';
import CreateRoundedIcon from '@mui/icons-material/CreateRounded';
import QuotationRequests from './QuotionRequests/QuotationRequests';
import useAddPurchaseOrder from '../../../../../../store/ReactQueries/procurements/purchaseOrders/useAddPurchaseOrder';
import usePurchaseOrderById from '../../../../../../store/ReactQueries/procurements/purchaseOrders/usePurchaseOrderById';
import useUpdatePurchaseOrder from '../../../../../../store/ReactQueries/procurements/purchaseOrders/useUpdatePurchaseOrder';
import useUpdateApprovalStatus, { UpdateStatusInput } from '../../../../../../store/ReactQueries/procurements/approvalRequests/useUpdateApprovalStatus';
import useApprovalRequestByPurchaseOrderId from '../../../../../../store/ReactQueries/procurements/approvalRequests/useApprovalRequestByPurchaseOrderId';
import { useNavigate, useParams } from 'react-router-dom';
import { EndPoints } from '../../../../../../utils/apiEndpoints';
import { FieldErrors } from '../../../../../../domain/helpers/Errors';
import { ApprovalStatus } from '../../../../../../enums/approvalStatus';
import { validateSchema } from '../../../../../../common/ValidateSchema';
import { useLanguageStore } from '../../../../../../store/useLanguageStore';
import { darkModeBackgroudInputs } from '../../../../../../utils/constants';
import { useDarkModeStore } from '../../../../../../store/useDarkModeStore';
import { PurchaseOrderStatus } from '../../../../../../enums/purchaseOrderStatus';
import { purchaseOrderSchema } from '../../../../../../domain/ValidationSchemas/PurchaseOrder/purchaseOrder';
import { InvoicesFileInterface, PurchaseOrderInterface } from '../../../../../../models/ProcurementModule/PurchateOrder/PurchaseOrder';

export type ModeTypes = "Add" | 'Edit' | 'View';

interface AddPurchaseOrderProps {
    mode: ModeTypes
}

export const AddPurchaseOrder: React.FC<AddPurchaseOrderProps> = ({ mode }) => {
    const isAdd = mode === 'Add';
    const isView = mode === 'View';

    const navigate = useNavigate();

    const { darkMode } = useDarkModeStore();
    const { language } = useLanguageStore();
    const { id: purchaseOrderId } = useParams<{ id: string; }>();
    const { data: purchaseOrderById, error } = usePurchaseOrderById(purchaseOrderId);
    const { data: approvalPersonsById } = useApprovalRequestByPurchaseOrderId(purchaseOrderId);

    const [loading, setLoading] = useState<boolean>(false);
    const [fileNames, setFileNames] = useState<{ fileUrl: string, fileName: string }[]>([]);

    const isRequestForQuotation = purchaseOrderById?.status === PurchaseOrderStatus.RequestForQuotation;
    const isApproved = purchaseOrderById?.status === PurchaseOrderStatus.Approved;

    const addPurchaseOrder = useAddPurchaseOrder(0, 0, fileNames);
    const updatePurchaseOrder = useUpdatePurchaseOrder(0, 0, fileNames);
    const updateApprovalStatus = useUpdateApprovalStatus(purchaseOrderId || '');

    const initialValue: PurchaseOrderInterface = {
        id: `New-Purchase-Order-${Math.random().toString()}`,
        description: '',
        code: '',
        date: new Date(),
        discount: undefined,
        purchaseOrderLines: [{
            id: `New-Row-${Math.random().toString()}`,
            title: '',
            quantity: 1,
            price: 1,
        }],
        purchaseOrderTypeId: '',
        requestForQuotation: false,
        vendorId: "",
        status: PurchaseOrderStatus.Pending,
        uploadInvoices: [],
        invoices: [],
        hasInvoice: false,
    }

    const [form, setForm] = useState<PurchaseOrderInterface>(initialValue);
    const [disabledSave, setDisabledSave] = useState<boolean>(false);
    const [errors, setErrors] = useState<FieldErrors>();

    useEffect(() => {
        if (mode === 'Add') setForm(initialValue);
        else if (purchaseOrderById) setForm(purchaseOrderById);
    }, [purchaseOrderById]);

    useEffect(() => {
        if (error) {
            ToastMessage.Failed(`Error: ${error.message}`);
            navigate(-1);
        }
    }, [error]);

    useEffect(() => {
        setLoading(true);

        const timer = setTimeout(() => {
            setLoading(false);
        }, 1000);

        return () => clearTimeout(timer);
    }, []);

    const preparePurchaseOrderData = (form: PurchaseOrderInterface): any => {
        const newItem: any = { ...form };

        delete newItem.author;
        delete newItem.modifiedBy;
        delete newItem.createdDate;
        delete newItem.updatedDate;
        delete newItem.purchaseOrderType;
        delete newItem.vendor;

        if (newItem.purchaseOrderLines && Array.isArray(newItem.purchaseOrderLines)) {
            newItem.purchaseOrderLines.forEach((line: any) => {
                if (line.id && line.id.includes('New-Row-')) {
                    delete line.id;
                    delete line.purchaseOrder;
                    delete line.purchaseOrderId;
                }
            });
        }
        newItem.invoices = newItem.invoices ? newItem.invoices.filter((item: InvoicesFileInterface) => !item.id.includes('New-File-')) : [];
        newItem.discount = newItem.discount ? newItem.discount : 0;
        return newItem;
    };

    const handleSave = async () => {
        const isValid = validateSchema(purchaseOrderSchema, form);
        setErrors(isValid.errors);
        if (!isValid.result) return;

        if (form.hasInvoice && (!form.uploadInvoices || form.uploadInvoices.length === 0 || !form.invoices || form.invoices.length === 0)) {
            setErrors((prevErrors) => ({
                ...prevErrors,
                hasInvoice: 'You need at least one file!',
            }));
            return;
        }

        setDisabledSave(true);

        let newItem = preparePurchaseOrderData(form);

        try {
            if (newItem.id.includes('New-Purchase-Order-')) {
                delete newItem.id;
                addPurchaseOrder.mutate(newItem, {
                    onSuccess: () => handleSuccess(),
                });
            } else {
                updatePurchaseOrder.mutate(newItem, {
                    onSuccess: () => handleSuccess(),
                });
            }
        } catch (ex: unknown) {
            if (ex instanceof Error) console.log(ex.message);
            else console.log("An unknown error occurred");
        }

        setTimeout(() => {
            setDisabledSave(false);
        }, 300);
    }

    const handleSuccess = () => navigate(EndPoints.PURCHASEORDERS);

    const handleSubmit = () => {
        const approvalPersonId = approvalPersonsById?.find(item => item.status === ApprovalStatus.RequestForQuotation);
        if (approvalPersonId && approvalPersonId.id) {
            const result: UpdateStatusInput = {
                id: approvalPersonId.id,
                status: ApprovalStatus.Pending,
                comment: ''
            }
            updateApprovalStatus.mutate(result, {
                onSuccess: () => navigate(`${EndPoints.PURCHASEORDERS}`),
                onError: () => ToastMessage.Failed('Something Went wrong')
            });
        }
    }

    return !form
        ? <LoadingIcon width={50} />
        : <div>
            <h3 className={`${darkMode ? 'text-white' : 'text-muted'}`}>
                <LocalOfferIcon className='me-2 mb-1' />
                {
                    mode === 'Add'
                        ? language['CreatePurchaseOrder']
                        : form.code ?? ''
                }
                {
                    isView
                    && <IconButton
                        className={`ms-2 ${darkMode && 'text-white'}`}
                        title={language['EditPurchaseOrder']}
                        onClick={() => navigate(`${EndPoints.PURCHASEORDERS}/edit/${form.id}`)}
                    >
                        <CreateRoundedIcon sx={{ width: 18, height: 18 }} />
                    </IconButton>
                }
            </h3>
            <hr />
            <div className='d-flex flex-column gap-2'>
                <BasicInformation
                    mode={mode}
                    form={form}
                    errors={errors}
                    loading={loading}
                    setForm={setForm}
                    isApproved={isApproved}
                    setFileNames={setFileNames}
                    isRequestForQuotation={isRequestForQuotation}
                />
                <AddProduct
                    mode={mode}
                    form={form}
                    errors={errors}
                    isApproved={isApproved}
                    isRequestForQuotation={isRequestForQuotation}
                    setForm={setForm}
                    loading={loading}
                />
                {
                    form.requestForQuotation
                    && !isAdd
                    && <QuotationRequests
                        mode={mode}
                        isApproved={isApproved}
                        purchaseOrderId={purchaseOrderId}
                    />
                }
                {
                    !isAdd
                    && <ApprovalPersonsTable
                        mode={mode}
                        form={form}
                    />
                }
                <div className='d-flex flex-row justify-content-end gap-2'>
                    <Button
                        className='font-open-sans'
                        sx={{
                            fontSize: "16px",
                            background: darkMode ? darkModeBackgroudInputs : "transparent",
                            border: `1px solid var(--btn-color)`,
                            color: darkMode ? 'white' : "var(--btn-color)",
                            "&:hover": {
                                background: darkMode ? darkModeBackgroudInputs : "transparent",
                                opacity: .7,
                            },
                        }}
                        onClick={() => navigate(EndPoints.PURCHASEORDERS)}
                    >
                        {language['Cancel']}
                    </Button>
                    {
                        isRequestForQuotation
                            ? <Button
                                className='font-open-sans'
                                sx={{
                                    fontSize: "16px",
                                    background: "var(--btn-color)",
                                    color: "white",
                                    width: 85,
                                    "&:hover": {
                                        background: "var(--btn-color)",
                                        opacity: .7
                                    },
                                    "&.Mui-disabled": {
                                        color: "white",
                                        cursor: "not-allowed",
                                        pointerEvents: "none",
                                    },
                                }}
                                onClick={handleSubmit}
                            >
                                Submit
                            </Button>
                            : !isView
                                ? <Button
                                    className='font-open-sans'
                                    sx={{
                                        fontSize: "16px",
                                        background: "var(--btn-color)",
                                        color: "white",
                                        width: 85,
                                        "&:hover": {
                                            background: "var(--btn-color)",
                                            opacity: .7
                                        },
                                        "&.Mui-disabled": {
                                            color: "white",
                                            cursor: "not-allowed",
                                            pointerEvents: "none",
                                        },
                                    }}
                                    onClick={handleSave}
                                >
                                    {disabledSave
                                        ? <LoadingIcon width={25} />
                                        : language["Save"]
                                    }
                                </Button>
                                : null
                    }
                </div>
            </div>
        </div>
}
export default AddPurchaseOrder;